/**
 * Methods for interactng with a ToDesktop application's tray icon.
 *
 * @remarks
 * This package exposes a number of methods for interactng with a ToDesktop
 * application's tray icon.
 *
 * The tray icon is a part of the operating systems UI, located:
 *
 * - at the right of the Menu Bar on macOS;
 * - in the System Tray on Windows.
 *
 * @experimental
 * @public
 * @packageDocumentation
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { construct, invoke } from "./invoke.js";
import { createOnAndOffNamespaceListeners, } from "./utils.js";
/**
 * Creates a new Tray.
 *
 * @param options - Tray constructor options.
 * @returns Identifier for the newly created Tray.
 * @public
 */
export async function create(...args) {
    return construct({
        namespace: "Tray",
        args,
    });
}
export async function setTitle(options) {
    if (typeof options === "string") {
        // eslint-disable-next-line no-console
        console.warn(`Calling 'setTitle' with a string is a legacy implementation that may be removed in future. 'setTitle' now expects an object with a 'ref' and 'title' property.`);
        const title = options;
        return window["todesktop"].tray.setTitle(title);
    }
    else {
        const { ref, title } = options;
        return invoke({
            instanceRef: ref,
            namespace: "Tray",
            method: "setTitle",
            args: [title],
        });
    }
}
export async function destroy(options) {
    if (options) {
        return invoke({
            instanceRef: options.ref,
            namespace: "Tray",
            method: "destroy",
            args: [],
        });
    }
    else {
        // eslint-disable-next-line no-console
        console.warn(`Calling 'destroy' without an options argument is a legacy implementation that may be removed in future. 'destroy' now expects an object with a 'ref' property.`);
        return window["todesktop"].tray.destroy();
    }
}
/**
 * Sets the image associated with this tray icon.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function setImage({ ref, image, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "setImage",
        args: [image],
    });
}
/**
 * Sets the image associated with this tray icon when pressed on macOS.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function setPressedImage({ ref, image, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "setImage",
        args: [image],
    });
}
/**
 * Sets the hover text for this tray icon.
 *
 * @param options - ref: Reference for the Tray; tooltip: hover text to display.
 * @public
 */
export async function setToolTip({ ref, tooltip, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "setToolTip",
        args: [tooltip],
    });
}
/**
 * Gets the title displayed next to the tray icon in the status bar.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function getTitle({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "getTitle",
        args: [],
    });
}
/**
 * Sets the option to ignore double click events. Ignoring these events allows you to detect every individual click of the tray icon.
 *
 * @param options - ref: Reference for the Tray; ignore: whether to ignore double clicks or not;
 * @public
 */
export async function setIgnoreDoubleClickEvents({ ref, ignore, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "setIgnoreDoubleClickEvents",
        args: [ignore],
    });
}
/**
 * Whether double click events will be ignored.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function getIgnoreDoubleClickEvents({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "getIgnoreDoubleClickEvents",
        args: [],
    });
}
/**
 * Displays a tray balloon on windows.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function displayBalloon(_a) {
    var { ref } = _a, options = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "displayBalloon",
        args: [options],
    });
}
/**
 * Removes a tray balloon.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function removeBalloon({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "removeBalloon",
        args: [],
    });
}
/**
 * Returns focus to the taskbar notification area. Windows only.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function focus({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "focus",
        args: [],
    });
}
/**
 * Pops up the context menu of the tray icon. When menu is passed, the menu will be shown instead of the tray icon's context menu. MacOS and Windows only.
 *
 * @param options - ref: Reference for the Tray; menu: Reference for the Menu; position: x and y coordinates.
 * @public
 */
export async function popUpContextMenu(_a) {
    var { ref } = _a, options = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "popUpContextMenu",
        args: [options.menu, options.position],
    });
}
/**
 * Closes an open context menu, as set by tray.setContextMenu(). MacOS and Windows only.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function closeContextMenu({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "closeContextMenu",
        args: [],
    });
}
/**
 * Sets the context menu for this icon.
 *
 * @param options - ref: Reference for the Tray; menu: Reference for the Menu;
 * @public
 */
export async function setContextMenu(_a) {
    var { ref } = _a, options = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "setContextMenu",
        args: [options.menu],
    });
}
/**
 * The bounds of this tray icon as Object. MacOS and Windows only.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function getBounds({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "getBounds",
        args: [],
    });
}
/**
 * Whether the tray icon is destroyed.
 *
 * @param options - ref: Reference for the Tray.
 * @public
 */
export async function isDestroyed({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Tray",
        method: "isDestroyed",
        args: [],
    });
}
const listeners = createOnAndOffNamespaceListeners("Tray");
/**
 * Subcribes to an event on a tray object.
 *
 * @param eventName - The event name to subscribe to.
 * @param callback - The callback function to execute when the event occurs.
 * @param options - ref: Reference for the Tray.
 * @returns event id.
 * @public
 */
export const addEventListener = listeners.addEventListener;
export const on = listeners.on;
/**
 * Unsusbcribes from an event on a tray object.
 *
 * @param options - ref: Reference for the Tray; eventName: The event name to unsubscribe from; callback: eventId: The eventId that was returned from the initial `tray.on` invocation.
 * @public
 */
export const removeEventListener = listeners.removeEventListener;
export const off = listeners.off;
/**
 * Unsubscribes all tray objects from the event name.
 *
 * @param options - ref: Reference for the Tray; eventName: The event name to unsubscribe from;
 * @returns Reference for the tray.
 * @public
 */
export const removeAllListeners = listeners.removeAllListeners;
